import React from 'react';
import { Check } from 'lucide-react';

export type CombinePhase = 'select' | 'anchor' | 'questions' | 'answers' | 'review';

const STEPS: { phase: CombinePhase; label: string }[] = [
  { phase: 'select', label: 'Select surveys' },
  { phase: 'anchor', label: 'Choose anchor' },
  { phase: 'questions', label: 'Map questions' },
  { phase: 'answers', label: 'Map answers' },
  { phase: 'review', label: 'Name & save' },
];

export type CombineProgressStepsProps = {
  phase: CombinePhase;
  /** Shown under Map questions / Map answers when there is more than one member survey. */
  memberStepIndex: number;
  memberCount: number;
};

export const CombineProgressSteps: React.FC<CombineProgressStepsProps> = ({
  phase,
  memberStepIndex,
  memberCount,
}) => {
  const activeIndex = STEPS.findIndex((s) => s.phase === phase);

  const subLabel =
    (phase === 'questions' || phase === 'answers') && memberCount > 1
      ? `Member survey ${memberStepIndex + 1} of ${memberCount}`
      : null;

  return (
    <nav
      aria-label="Combine surveys progress"
      className="w-full overflow-visible"
    >
      <div className="mx-auto flex w-full max-w-5xl justify-center">
        <div className="flex w-full min-w-0 flex-1 items-start gap-0">
          {STEPS.map((step, i) => {
            const isComplete = i < activeIndex;
            const isCurrent = i === activeIndex;
            const isUpcoming = i > activeIndex;

            const circleClass = [
              'relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors',
              isComplete && 'border-emerald-600 bg-emerald-600 text-white',
              isCurrent && 'border-blue-600 bg-white text-blue-700 ring-2 ring-blue-200 ring-offset-2 ring-offset-transparent',
              isUpcoming && 'border-gray-300 bg-white text-gray-400',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div
                key={step.phase}
                className="flex min-w-0 flex-1 flex-col items-center"
              >
                {/* Circle row: spacers + circle so the circle is centred in this column */}
                <div className="flex w-full min-h-[2.75rem] items-center justify-center overflow-visible">
                  {i > 0 ? (
                    <div
                      className={[
                        'h-0.5 min-w-[4px] flex-1 rounded-full',
                        activeIndex >= i ? 'bg-emerald-500' : 'bg-gray-200',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-hidden
                    />
                  ) : (
                    <div
                      className="min-w-0 flex-1"
                      aria-hidden
                    />
                  )}
                  <div
                    className={circleClass}
                    aria-current={isCurrent ? 'step' : undefined}
                  >
                    {isComplete ? (
                      <Check
                        className="h-4 w-4 shrink-0 text-gray-800"
                        strokeWidth={2.5}
                        aria-hidden
                      />
                    ) : (
                      <span>
                        {i + 1}
                      </span>
                    )}
                  </div>
                  {i < STEPS.length - 1 ? (
                    <div
                      className={[
                        'h-0.5 min-w-[4px] flex-1 rounded-full',
                        activeIndex > i ? 'bg-emerald-500' : 'bg-gray-200',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-hidden
                    />
                  ) : (
                    <div
                      className="min-w-0 flex-1"
                      aria-hidden
                    />
                  )}
                </div>

                {/* Label directly under this column’s circle */}
                <div className="mt-2 flex w-full max-w-full flex-col items-center px-0.5 text-center">
                  <span
                    className={[
                      'w-full text-[11px] font-medium leading-snug sm:text-xs',
                      isComplete && 'text-emerald-800',
                      isCurrent && 'text-gray-900',
                      isUpcoming && 'text-gray-400',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {step.label}
                  </span>
                  {isCurrent && subLabel ? (
                    <span className="mt-0.5 w-full max-w-[9rem] text-[10px] leading-tight text-gray-500">
                      {subLabel}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
